// Co-editing reconciliation for material_mapping (ENG-539 Stage 2).
//
// The collab service models the mapping as a Y.Array of row objects
//   { meshIndex, meshName, primitiveIndex, materialId?, hidden? }
// keyed logically by (meshIndex, primitiveIndex) — a mesh slot. Two artists
// editing DIFFERENT slots must both converge, so we never blind-replace the
// whole array; we apply the minimal PER-ROW ops (set / insert / delete) inside
// one Yjs transaction. Pure logic lives here (no Yjs import) so it's trivially
// unit-testable; CollabPresenceClient calls applyMappingToYArray with a real
// Y.Array.

// The shared Yjs key — MUST match the collab service's docModel MAPPING_KEY.
export const MAPPING_KEY = 'materialMapping'

// slotKey is the stable identity of a mapping row — a mesh primitive slot.
export function slotKey(row) {
  return `${row.meshIndex}:${row.primitiveIndex ?? 0}`
}

// dedupBySlot collapses any duplicate rows for the same slot to the last one.
// Concurrent edits to the SAME slot can leave two rows in the Y.Array (a known
// per-row-replace CRDT artifact); the app keys by slot, so on read we keep the
// last occurrence (stable, deterministic) and drop earlier dupes.
export function dedupBySlot(rows) {
  const byKey = new Map()
  for (const row of rows || []) byKey.set(slotKey(row), row)
  return [...byKey.values()]
}

// reconcileRemoteMapping — decide what to apply when the shared collab doc
// pushes a mapping to this client. The Y.Array starts EMPTY and its first
// observe fires [] before anyone seeds it from the server mapping; blindly
// applying that wiped the server-loaded slots ("Myra material slots
// disappeared"). A remote mapping never legitimately shrinks to empty while we
// hold a populated one — unbinding a slot keeps its row (only clearing
// materialId) — so an empty-over-populated update is the uninitialised-doc
// artifact. Returns the rows to apply, or null to signal "ignore this update".
export function reconcileRemoteMapping(localRows, remoteRows) {
  const remote = Array.isArray(remoteRows) ? remoteRows : []
  const local = Array.isArray(localRows) ? localRows : []
  if (remote.length === 0 && local.length > 0) return null
  return remote
}

// rowsEqual — shallow value equality of the fields we persist, so an unchanged
// row produces no Yjs op (no needless broadcast / server write).
export function rowsEqual(a, b) {
  if (!a || !b) return a === b
  return (
    a.meshIndex === b.meshIndex &&
    (a.primitiveIndex ?? 0) === (b.primitiveIndex ?? 0) &&
    (a.meshName ?? null) === (b.meshName ?? null) &&
    (a.materialId ?? null) === (b.materialId ?? null) &&
    (a.hidden ?? false) === (b.hidden ?? false)
  )
}

// diffMapping computes the per-row ops that turn `current` (the array's present
// contents, in order) into `next` (the editor's desired mapping). Returns
// { updates:[{index,row}], inserts:[row], deletes:[index] } with delete indices
// descending so they can be applied without reindexing hazards. Order of
// existing rows is preserved; new slots append.
export function diffMapping(current, next) {
  const curByKey = new Map()
  ;(current || []).forEach((row, index) => curByKey.set(slotKey(row), { row, index }))
  const nextByKey = new Map()
  ;(next || []).forEach((row) => nextByKey.set(slotKey(row), row))

  const updates = []
  const inserts = []
  const deletes = []

  for (const [key, nrow] of nextByKey) {
    const cur = curByKey.get(key)
    if (!cur) inserts.push(nrow)
    else if (!rowsEqual(cur.row, nrow)) updates.push({ index: cur.index, row: nrow })
  }
  for (const [key, { index }] of curByKey) {
    if (!nextByKey.has(key)) deletes.push(index)
  }
  deletes.sort((a, b) => b - a) // descending — safe to delete in sequence
  return { updates, inserts, deletes }
}

// applyMappingToYArray applies `next` onto a live Y.Array<MappingRow> using the
// minimal per-row ops, in a single transaction (origin tags the write so the
// local observer can ignore its own echo). Returns the number of ops applied.
// yarray: a yjs Y.Array; transact: doc.transact bound to the array's doc.
export function applyMappingToYArray(yarray, next, transact, origin) {
  const current = yarray.toArray()
  const { updates, inserts, deletes } = diffMapping(current, next)
  if (!updates.length && !inserts.length && !deletes.length) return 0
  const run = () => {
    // Replace-in-place = delete + insert at the same index (Y.Array has no set).
    for (const { index, row } of updates) {
      yarray.delete(index, 1)
      yarray.insert(index, [row])
    }
    for (const index of deletes) yarray.delete(index, 1)
    if (inserts.length) yarray.push(inserts)
  }
  if (transact) transact(run, origin)
  else run()
  return updates.length + inserts.length + deletes.length
}
